import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CrudForm } from '../shared/CrudPage';
import { enumEntries, invoiceService, paymentService } from '@kss/api';
import type { Invoice, Payment, PaymentCreate, PaymentFilter } from '@kss/api';
import { loadStoreOptions, loadInvoiceOptions, loadCustomerOptions } from '../shared/formLoaders';

/**
 * Payment form — wired to the live schema verified 2026-05-05.
 *
 * Live schema: paymentNumber, paymentDate, mode (int enum), amount,
 * referenceNumber, notes, invoiceId, customerId, storeId.
 *
 * Fixes vs the legacy form:
 *   - `customerId` now loads from `customerService.all()` (was on
 *     `loadUserOptions`).
 *   - `method` (string) → `mode` (int enum: 1=Cash, 2=Card, 3=UPI,
 *     4=NetBanking, 5=Cheque, 6=Wallet, 7=Other).
 *   - `reference` → `referenceNumber`.
 *   - `description` → `notes`.
 *   - Dropped `salesOrderId` (not on Payment schema) and `isActive`
 *     (Payment has no per-row toggle — refunds are separate rows).
 *
 * SSO Issue #10 (2026-07-13):
 *   - #10a: `AllPayments` now surfaces pending receivables so the user
 *     can jump into this form via `?invoiceId=…&customerId=…&amount=…`.
 *   - #10b: Selecting an Invoice here now auto-fills `customerId`
 *     (from `invoice.customerId`) and defaults `amount` to the
 *     invoice's outstanding `balanceAmount`. Manual edits after
 *     autofill are preserved — the hook only fires on invoice-picker
 *     changes, so subsequent edits to `customerId` / `amount` stand.
 */

/* Single source of truth — see enums.ts PaymentModeLabels. */
const MODE_BY_LABEL: Record<string, number> = Object.fromEntries(
  enumEntries('PaymentMode').map((e) => [e.label, e.value]),
);
const LABEL_BY_MODE: Record<number, string> = Object.fromEntries(
  Object.entries(MODE_BY_LABEL).map(([k, v]) => [v, k]),
);

type F = {
  paymentNumber: string;
  paymentDate: string;
  amount: number;
  mode: string;
  referenceNumber: string;
  notes: string;
  invoiceId: string;
  customerId: string;
  storeId: string;
};
const today = new Date().toISOString().slice(0, 10);
const empty: F = {
  paymentNumber: '',
  paymentDate: today,
  amount: 0,
  mode: 'Cash',
  referenceNumber: '',
  notes: '',
  invoiceId: '',
  customerId: '',
  storeId: '',
};

/* Module-level cache of the invoice list keyed by invoiceId. Populated
   once per session — the same cache backs both the initial URL-param
   hydration and the invoice-picker onChange autofill so the form
   never re-hits the network to look up a row it just showed. */
const invoiceCache: {
  promise: Promise<Map<string, Invoice>> | null;
  byId: Map<string, Invoice>;
} = { promise: null, byId: new Map() };

function loadInvoiceIndex(): Promise<Map<string, Invoice>> {
  if (invoiceCache.byId.size > 0) return Promise.resolve(invoiceCache.byId);
  if (invoiceCache.promise) return invoiceCache.promise;
  invoiceCache.promise = (async () => {
    try {
      const rows = await invoiceService.all();
      const m = new Map<string, Invoice>();
      for (const inv of Array.isArray(rows) ? rows : []) {
        const k = inv.invoiceId ?? '';
        if (k) m.set(k, inv);
      }
      invoiceCache.byId = m;
      return m;
    } catch {
      // Leave the cache empty; autofill silently no-ops until the
      // next successful load. The form still works — the user just
      // types the customer + amount themselves.
      return new Map<string, Invoice>();
    } finally {
      invoiceCache.promise = null;
    }
  })();
  return invoiceCache.promise;
}

export default function NewPayment() {
  const [searchParams] = useSearchParams();
  const qpInvoiceId = searchParams.get('invoiceId') ?? '';
  const qpCustomerId = searchParams.get('customerId') ?? '';
  const qpAmount = searchParams.get('amount') ?? '';

  /* Ref-based invoice index so the field-level onChange closure can
     read the latest cached map without triggering re-renders. */
  const invoiceIndexRef = useRef<Map<string, Invoice>>(invoiceCache.byId);
  const [, forceTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    loadInvoiceIndex().then((m) => {
      if (cancelled) return;
      invoiceIndexRef.current = m;
      // Only re-render if the deep-link came in with an invoiceId
      // that the cache didn't have on the first paint — otherwise
      // the form is already sitting on the correct autofilled state.
      if (qpInvoiceId && m.has(qpInvoiceId)) forceTick((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [qpInvoiceId]);

  const initialForm = useMemo<Partial<F>>(() => {
    // Query params win when present. If the invoice is already in
    // the module cache (common on same-session navigations from
    // AllPayments → NewPayment), fill blanks the URL didn't carry.
    const patch: Partial<F> = {};
    if (qpInvoiceId) patch.invoiceId = qpInvoiceId;
    if (qpCustomerId) patch.customerId = qpCustomerId;
    if (qpAmount) {
      const n = Number(qpAmount);
      if (Number.isFinite(n) && n > 0) patch.amount = n;
    }
    if (qpInvoiceId) {
      const inv = invoiceCache.byId.get(qpInvoiceId);
      if (inv) {
        if (!patch.customerId && inv.customerId) patch.customerId = inv.customerId;
        if (patch.amount == null && inv.balanceAmount != null) {
          patch.amount = Number(inv.balanceAmount);
        }
      }
    }
    return patch;
  }, [qpInvoiceId, qpCustomerId, qpAmount]);

  return (
    <CrudForm<Payment, PaymentCreate, PaymentFilter, F>
      title="payment"
      service={paymentService}
      idKey="paymentId"
      basePath="/panel/payments"
      emptyForm={empty}
      initialForm={initialForm}
      fields={[
        { key: 'paymentNumber', label: 'Payment number', required: true, placeholder: 'PAY-1001' },
        { key: 'paymentDate', label: 'Payment date', type: 'date', required: true },
        { key: 'amount', label: 'Amount (₹)', type: 'number', required: true },
        { key: 'mode', label: 'Method', type: 'select', options: Object.keys(MODE_BY_LABEL).map((v) => ({ value: v, label: v })) },
        { key: 'referenceNumber', label: 'Reference', placeholder: 'Txn ID' },
        {
          key: 'invoiceId',
          label: 'Invoice',
          type: 'search',
          loadOptions: loadInvoiceOptions,
          placeholder: 'Link an invoice',
          /* SSO Issue #10b — when the user picks an invoice, prefill
             the customer + outstanding amount + today's date. Only
             fires on invoice change so any manual customer/amount
             edits the user makes afterwards are preserved. */
          onChange: (value, nextForm) => {
            const id = String(value ?? '');
            if (!id) return;
            const inv = invoiceIndexRef.current.get(id);
            if (!inv) return;
            const patch: Partial<F> = {};
            if (inv.customerId) patch.customerId = inv.customerId;
            const bal = Number(inv.balanceAmount ?? 0);
            if (Number.isFinite(bal) && bal > 0) patch.amount = bal;
            // Only stamp today's date if the user hasn't already set
            // one explicitly (respects an edit-from-history flow).
            if (!nextForm.paymentDate) patch.paymentDate = today;
            return patch;
          },
        },
        { key: 'customerId', label: 'Customer', type: 'search', loadOptions: loadCustomerOptions, placeholder: 'Pick a customer' },
        { key: 'storeId', label: 'Store', type: 'search', loadOptions: loadStoreOptions, placeholder: 'Select a store' },
        { key: 'notes', label: 'Notes', type: 'textarea', fullWidth: true },
      ]}
      fromEntity={(r) => ({
        paymentNumber: r.paymentNumber ?? '',
        paymentDate: r.paymentDate?.slice(0, 10) ?? today,
        amount: Number(r.amount ?? 0),
        mode:
          typeof r.mode === 'number'
            ? LABEL_BY_MODE[r.mode] ?? 'Cash'
            : String(r.mode ?? r.method ?? 'Cash'),
        referenceNumber: r.referenceNumber ?? r.reference ?? '',
        notes: r.notes ?? r.description ?? '',
        invoiceId: r.invoiceId ?? '',
        customerId: r.customerId ?? '',
        storeId: r.storeId ?? '',
      })}
      toEntity={(f, id) => ({
        paymentId: id,
        paymentNumber: f.paymentNumber,
        paymentDate: f.paymentDate || undefined,
        amount: f.amount,
        mode: MODE_BY_LABEL[f.mode] ?? 1,
        referenceNumber: f.referenceNumber || undefined,
        notes: f.notes || undefined,
        invoiceId: f.invoiceId || undefined,
        customerId: f.customerId || undefined,
        storeId: f.storeId || undefined,
      })}
      validate={(f) =>
        !f.paymentNumber.trim()
          ? 'Payment number is required'
          : f.amount <= 0
          ? 'Amount must be > 0'
          : null
      }
    />
  );
}
